class MCartDrawer extends HTMLElement {
  constructor() {
    super()
    this.cartDrawerInner = this.querySelector(".m-cart-drawer__inner")
    this.cartDrawerCloseIcon = this.querySelector(".m-cart-drawer__close")
    this.cartOverlay = this.querySelector(".m-cart__overlay")
    this.rootUrl = window.Shopify.routes.root
    this.externalCartRefreshTimer = null

    this.setHeaderCartIconAccessibility()
    this.setupExternalCartSync()

    this.cartDrawerCloseIcon.addEventListener("click", this.close.bind(this))
    this.addEventListener("click", (event) => {
      if (event.target.closest(".m-cart-drawer__inner") !== this.cartDrawerInner) this.close()
    })
  }

  static isCartMutationRequest(url) {
    if (!url) return false
    const requestUrl = String(url)
    return /\/cart\/(add|change|update|clear)(\.js|\.json)?(?:\?|$)/.test(requestUrl)
  }

  static setupExternalCartMutationObserver() {
    if (window.__minimogCartMutationObserverInstalled) return
    window.__minimogCartMutationObserverInstalled = true

    const notifyExternalUpdate = (url) => {
      document.dispatchEvent(
        new CustomEvent("minimog:cart-external-updated", {
          detail: { url },
        })
      )
    }

    if (typeof window.fetch === "function") {
      const originalFetch = window.fetch.bind(window)
      window.fetch = (...args) =>
        originalFetch(...args).then((response) => {
          const request = args[0]
          const requestUrl =
            typeof request === "string" ? request : request && request.url ? request.url : ""
          if (response && response.ok && MCartDrawer.isCartMutationRequest(requestUrl)) {
            notifyExternalUpdate(requestUrl)
          }
          return response
        })
    }

    if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
      const originalOpen = window.XMLHttpRequest.prototype.open
      const originalSend = window.XMLHttpRequest.prototype.send

      window.XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.__minimogCartMutationUrl = url
        return originalOpen.call(this, method, url, ...rest)
      }

      window.XMLHttpRequest.prototype.send = function (...args) {
        this.addEventListener("load", () => {
          if (
            this.status >= 200 &&
            this.status < 300 &&
            MCartDrawer.isCartMutationRequest(this.__minimogCartMutationUrl)
          ) {
            notifyExternalUpdate(this.__minimogCartMutationUrl)
          }
        })
        return originalSend.call(this, ...args)
      }
    }
  }

  setupExternalCartSync() {
    MCartDrawer.setupExternalCartMutationObserver()

    this.externalCartSyncHandler = () => {
      clearTimeout(this.externalCartRefreshTimer)
      this.externalCartRefreshTimer = setTimeout(() => {
        this.onCartDrawerUpdate()
      }, 150)
    }

    ;["minimog:cart-external-updated", "cart:refresh", "cart:updated", "cart-updated"].forEach(
      (eventName) => {
        document.addEventListener(eventName, this.externalCartSyncHandler)
      }
    )
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll(".m-cart-icon-bubble").forEach((cartIcon) => {
      cartIcon.setAttribute("role", "button")
      cartIcon.setAttribute("aria-haspopup", "dialog")
      cartIcon.addEventListener("click", (event) => {
        if (!MinimogSettings.enable_cart_drawer) return
        event.preventDefault()
        this.open(cartIcon)
      })
    })
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy)
    this.classList.add("m-cart-drawer--active")
    requestAnimationFrame(() => {
      this.style.setProperty("--m-bg-opacity", "0.5")
      this.cartDrawerInner.style.setProperty("--translate-x", "0")
    })
    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer)
    document.documentElement.classList.add("prevent-scroll")
  }

  close() {
    this.style.setProperty("--m-bg-opacity", "0")
    this.cartDrawerInner.style.setProperty("--translate-x", "100%")
    setTimeout(() => {
      this.classList.remove("m-cart-drawer--active")
      document.documentElement.classList.remove("prevent-scroll")
    }, 300)
  }

  renderContents(data) {
    if (this.classList.contains("m-cart--empty")) this.classList.remove("m-cart--empty")
    this.productId = data.id
    this.getSectionsToRender().forEach((section) => {
      const targetNode = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id)
      targetNode.innerHTML = this.getSectionInnerHTML(data.sections[section.id], section.selector)
    })
    setTimeout(() => {
      this.open()
    })
  }

  updateCartCount(itemCount) {
    document.querySelectorAll(".m-cart-count-bubble").forEach((cartCountBubble) => {
      if (itemCount > 0) {
        cartCountBubble.textContent = itemCount
        cartCountBubble.classList.remove("m:hidden")
      } else {
        cartCountBubble.classList.add("m:hidden")
      }
    })
  }

  getCart() {
    return fetchJSON(this.rootUrl + "cart.json")
  }

  onCartDrawerUpdate(shouldRenderCartFooter = true) {
    fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        this.getSectionsToRender().forEach((section) => {
          const targetNode = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id)
          if ("cart-items" === section.block || shouldRenderCartFooter) {
            targetNode.innerHTML = this.getSectionInnerHTML(responseText, section.selector)
          }
        })
      })
      .catch(() => {})

    this.getCart().then((cart) => {
      this.classList.toggle("m-cart--empty", cart.item_count === 0)
      this.updateCartCount(cart.item_count)
    })
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: "[data-minimog-cart-items]", block: "cart-items" },
      { id: "cart-drawer", selector: "[data-minimog-cart-discounts]", block: "cart-footer" },
      { id: "cart-drawer", selector: "[data-cart-subtotal]", block: "cart-footer" },
      { id: "cart-drawer", selector: "[data-minimog-gift-wrapping]", block: "cart-footer" },
    ]
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector)
  }

  setActiveElement(activeElement) {
    this.activeElement = activeElement
  }
}

customElements.define("m-cart-drawer", MCartDrawer)

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      { id: "MinimogCartDrawer", section: "cart-drawer", selector: "[data-minimog-cart-items]" },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
      },
      { id: "MinimogCartDrawer", section: "cart-drawer", selector: "[data-cart-subtotal]" },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
      },
    ]
  }
}

customElements.define("m-cart-drawer-items", MCartDrawerItems)

